import React, {
  ChangeEventHandler,
  FocusEvent,
  FocusEventHandler,
} from "react";

import { useFocusHandler } from "@covertree/hooks";
import {
  type InputBaseComponentProps,
  TextField,
  Theme,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import { useDate } from "../";
import { TextFieldProps } from "@mui/material/TextField/TextField";
import { mergeSx } from "merge-sx";

export interface CreateInputProps {
  name: string;
  error?: boolean;
  placeholder: string;
  sx?: TextFieldProps["sx"];
  inputProps?: InputBaseComponentProps;
  nextFocus?: string;
  previousFocus?: string;
  onChange?: ChangeEventHandler;
  onFocus?: FocusEventHandler;
  onBlur?: FocusEventHandler;
  disabled?: boolean;
  zeroPrefixDisabled?: boolean;
  fontSize?: number;
}
export interface CustomBlurEventConfig {
  zeroPrefixDisabled: boolean;
  updater: (value: string) => void;
}

export type CustomBlurEventHandler = (
  e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  config: CustomBlurEventConfig
) => void;
export interface InputOptions {
  maxLength: number;
  fieldName: "day" | "month" | "year";
  updaterName: "changeDay" | "changeMonth" | "changeYear";
  sx: TextFieldProps["sx"];
  customOnBlur?: CustomBlurEventHandler;
}

export const createInput =
  ({
    sx,
    fieldName,
    maxLength,
    updaterName,
    customOnBlur,
  }: InputOptions): React.FC<CreateInputProps> =>
  // eslint-disable-next-line react/display-name
  ({
    name,
    error,
    placeholder,
    sx: finalSx,
    inputProps,
    nextFocus,
    previousFocus,
    onChange,
    onFocus,
    onBlur,
    zeroPrefixDisabled = false,
    disabled = false,
    fontSize,
    ...props
  }) => {
    const fontSizeValue = fontSize ? `${fontSize}px` : undefined;
    const theme = useTheme();
    const isMobileView = useMediaQuery<Theme>(theme.breakpoints.down("md"));

    const { [fieldName]: state, [updaterName]: updater } = useDate();
    const { setFocused, handlePreviousFocus } = useFocusHandler(
      state.length === maxLength,
      state.length === 0,
      nextFocus,
      previousFocus
    );

    return (
      <TextField
        variant="outlined"
        error={error}
        inputProps={{ maxLength, name, ...inputProps }}
        disabled={disabled}
        {...props}
        placeholder={placeholder}
        sx={mergeSx(
          sx,
          fontSize
            ? {
                fontSize: fontSizeValue,
                "& .MuiInputBase-root": {
                  height: fontSize * 3,
                },
                "& .MuiOutlinedInput-root": {
                  minHeight: fontSize * 3,
                },
                "& .MuiInputBase-input": {
                  fontSize: fontSizeValue,
                },
              }
            : undefined,
          finalSx
        )}
        value={state}
        type={isMobileView ? "tel" : "text"}
        autoComplete="off"
        onChange={(e) => {
          // Date segments (MM/DD/YYYY) are numeric only — drop any other input.
          updater(e.target.value.replace(/\D/g, ""));
          return onChange?.(e);
        }}
        onFocus={(e) => {
          setFocused(true);
          return onFocus?.(e);
        }}
        onBlur={(e) => {
          setFocused(false);
          customOnBlur?.(e, { zeroPrefixDisabled, updater });
          return onBlur?.(e);
        }}
        onKeyDown={handlePreviousFocus}
      />
    );
  };
